import { useId, useState } from 'react';

import { Button, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { loiTheoConTro } from '../../vai-tro/nghiep-vu/vai-tro';
import { MO_CHE_DO, NHAN_CHE_DO, NHAN_LOAI } from '../constants';
import { MA_TOI_DA, chuanHoaMa, kiemMa } from '../nghiep-vu/loai-chung-tu';
import { DOCUMENT_TYPES, EXTRACTION_MODES } from '../types';
import type { DocumentType, ExtractionMode, OcrSchemaCreate } from '../types';
import { ChonModel } from './ChonModel';

const O_TREN_FORM = /^\/(code|name|type|description|extraction_mode|ocr_provider|ocr_reader_provider)$/;

/**
 * Hộp tạo loại chứng từ (`POST /schemas`). Chỉ phần cấp loại chứng từ; trường và bảng thêm ở màn
 * chi tiết (gửi `fields: []`, `tables: []`). Lỗi của server hiện dưới đúng ô theo `pointer`, lỗi
 * khác trong hộp; hộp không đóng khi lỗi.
 */
export function HopLoaiChungTu({
  open,
  dangGui,
  onClose,
  onTao,
}: {
  open: boolean;
  dangGui: boolean;
  onClose: () => void;
  onTao: (du: OcrSchemaCreate) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const [ma, setMa] = useState('');
  const [ten, setTen] = useState('');
  const [loai, setLoai] = useState<DocumentType>('INVOICE');
  const [moTa, setMoTa] = useState('');
  const [cheDo, setCheDo] = useState<ExtractionMode>('SINGLE_MODEL');
  const [modelDien, setModelDien] = useState<string | null>(null);
  const [modelDoc, setModelDoc] = useState<string | null>(null);
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: ApiError; truong: Record<string, string> } | null>(null);

  const maLoi = kiemMa(ma);
  const loiMa =
    (daGui && maLoi ? t(maLoi === 'trong' ? 'cauHinhOcr.fMaTrong' : 'cauHinhOcr.fMaSai') : undefined) ??
    loiServer?.truong['/code'];
  const loiTen = (daGui && !ten.trim() ? t('cauHinhOcr.fTenTrong') : undefined) ?? loiServer?.truong['/name'];
  const loiDoc =
    (daGui && cheDo === 'OCR_THEN_MAP' && !modelDoc ? t('cauHinhOcr.fModelDocTrong') : undefined) ??
    loiServer?.truong['/ocr_reader_provider'];
  const loiKhac = Object.entries(loiServer?.truong ?? {}).filter(([d]) => !O_TREN_FORM.test(d));
  const coLoiTheoO = loiServer && Object.keys(loiServer.truong).length > 0 && loiKhac.length === 0;
  const doi = () => setLoiServer(null);

  const gui = async () => {
    setDaGui(true);
    if (maLoi || !ten.trim() || (cheDo === 'OCR_THEN_MAP' && !modelDoc) || dangGui) return;
    setLoiServer(null);
    try {
      await onTao({
        code: ma,
        name: ten.trim(),
        type: loai,
        description: moTa.trim() || null,
        extraction_mode: cheDo,
        ocr_provider: modelDien,
        ocr_reader_provider: cheDo === 'OCR_THEN_MAP' ? modelDoc : null,
        fields: [],
        tables: [],
      });
    } catch (err) {
      setLoiServer({ loi: err as ApiError, truong: loiTheoConTro((err as ApiError).details) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('cauHinhOcr.taoTieuDe')}
      width={640}
      destroyOnHidden
      footer={
        <div className="space-y-3 text-left">
          {loiServer && (
            <LoiTrongHop>
              {coLoiTheoO ? t('cauHinhOcr.loiTheoO') : dichLoi(loiServer.loi)}
              {loiKhac.map(([d, c]) => (
                <span key={d} className="mt-1 block">
                  <span className="font-mono">{d}</span>: {c}
                </span>
              ))}
            </LoiTrongHop>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangGui}>
              {dangGui ? t('cauHinhOcr.dangTao') : t('cauHinhOcr.taoLoai')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('cauHinhOcr.fMa')} required hint={t('cauHinhOcr.fMaGoiY')} error={loiMa}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                autoComplete="off"
                className="font-mono"
                maxLength={MA_TOI_DA}
                placeholder="HDVAT-DAU-VAO"
                value={ma}
                onChange={(e) => {
                  setMa(chuanHoaMa(e.target.value));
                  doi();
                }}
              />
            )}
          </Field>
          <Field label={t('cauHinhOcr.fLoai')} required error={loiServer?.truong['/type']}>
            {(p) => (
              <Select<DocumentType>
                {...p}
                className="w-full"
                value={loai}
                onChange={(v) => {
                  setLoai(v);
                  doi();
                }}
                options={DOCUMENT_TYPES.map((v) => ({ value: v, label: t(NHAN_LOAI[v]) }))}
              />
            )}
          </Field>
        </div>
        <Field label={t('cauHinhOcr.fTen')} required error={loiTen}>
          {(p) => (
            <Input
              {...p}
              maxLength={255}
              placeholder={t('cauHinhOcr.fTenGoiY')}
              value={ten}
              onChange={(e) => {
                setTen(e.target.value);
                doi();
              }}
            />
          )}
        </Field>
        <Field label={t('cauHinhOcr.fMoTa')} hint={t('cauHinhOcr.fMoTaGoiY')} error={loiServer?.truong['/description']}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={moTa}
              onChange={(e) => {
                setMoTa(e.target.value);
                doi();
              }}
            />
          )}
        </Field>
        <Field label={t('cauHinhOcr.fCheDo')} hint={t(MO_CHE_DO[cheDo])} error={loiServer?.truong['/extraction_mode']}>
          {(p) => (
            <Select<ExtractionMode>
              {...p}
              className="w-full"
              value={cheDo}
              onChange={(v) => {
                setCheDo(v);
                doi();
              }}
              options={EXTRACTION_MODES.map((v) => ({ value: v, label: t(NHAN_CHE_DO[v]) }))}
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          {cheDo === 'OCR_THEN_MAP' && (
            <Field label={t('cauHinhOcr.fModelDoc')} required error={loiDoc}>
              {(p) => (
                <ChonModel
                  {...p}
                  vai="doc"
                  value={modelDoc}
                  onChange={(v) => {
                    setModelDoc(v);
                    doi();
                  }}
                />
              )}
            </Field>
          )}
          <Field
            label={t(cheDo === 'OCR_THEN_MAP' ? 'cauHinhOcr.fModelDien' : 'cauHinhOcr.fModelMot')}
            error={loiServer?.truong['/ocr_provider']}
          >
            {(p) => (
              <ChonModel
                {...p}
                vai="dien"
                value={modelDien}
                onChange={(v) => {
                  setModelDien(v);
                  doi();
                }}
              />
            )}
          </Field>
        </div>
      </form>
    </Dialog>
  );
}
